import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';

interface TablePaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /**
   * Tryb „tylko licznik" — renderuje wyłącznie liczbę wyników, bez wyboru „wierszy na stronie"
   * i bez strzałek stron. Używany w widoku kalendarza, gdzie paginacja nie ma sensu (nawigacja
   * idzie miesiącami, a zapytanie jest już zawężone do widocznego miesiąca).
   */
  countOnly?: boolean;
}

/**
 * Stopka pagera listy — wygląd wg Figmy (redesign Nimbus): po lewej podsumowanie
 * „Pokazano X–Y z Z wyników"; po prawej nawigacja stron (tylko poprzednia/następna
 * strzałka + numery, aktywny numer w obwódkowym boksie akcentu) oraz osobna pastylka
 * z liczbą rekordów na stronę („10 / stronę"). Bez skoku do pierwszej/ostatniej strony.
 */
export function TablePagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  countOnly = false,
}: TablePaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : page * pageSize + 1;
  const endItem = Math.min((page + 1) * pageSize, totalItems);

  const canGoPrevious = page > 0;
  const canGoNext = page < totalPages - 1;

  if (countOnly) {
    return (
      <div className="mx-7 flex items-center border-t border-border-secondary py-4">
        <span className="text-[13px] text-text-tertiary" data-testid="pagination-count-only">
          {t('common.pagination.resultsCount')}{' '}
          <span className="font-medium text-text-secondary">{totalItems}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="mx-7 flex flex-wrap items-center justify-between gap-3 border-t border-border-secondary py-4">
      {/* Lewa: podsumowanie zakresu wyników. */}
      <span className="text-[13px] text-text-tertiary" data-testid="pagination-summary">
        {t('common.pagination.showing', { from: startItem, to: endItem, total: totalItems })}
      </span>

      {/* Prawa: nawigacja stron + liczba rekordów na stronę. */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          {/* Poprzednia strona. */}
          <button
            type="button"
            onClick={() => onPageChange?.(page - 1)}
            disabled={!canGoPrevious}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            title={t('common.pagination.prevAria')}
            aria-label={t('common.pagination.prevAria')}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>

          {/* Numery stron (z wielokropkiem przy dużej liczbie stron). */}
          {generatePageNumbers(page, totalPages).map((pageNum, idx) => {
            if (pageNum === -1) {
              return (
                <span key={`ellipsis-${idx}`} className="px-1.5 text-text-tertiary">
                  …
                </span>
              );
            }
            const isActive = pageNum === page;
            return (
              <button
                key={pageNum}
                type="button"
                onClick={() => onPageChange?.(pageNum)}
                aria-current={isActive ? 'page' : undefined}
                className={`inline-flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600 ${
                  isActive
                    ? 'border-accent-300 bg-accent-50 text-accent-600 dark:border-accent-500 dark:bg-accent-900/20 dark:text-accent-300'
                    : 'border-transparent text-text-secondary hover:bg-bg-secondary'
                }`}
              >
                {pageNum + 1}
              </button>
            );
          })}

          {/* Następna strona. */}
          <button
            type="button"
            onClick={() => onPageChange?.(page + 1)}
            disabled={!canGoNext}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            title={t('common.pagination.nextAria')}
            aria-label={t('common.pagination.nextAria')}
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>

        {onPageSizeChange && (
          <Dropdown
            options={pageSizeOptions.map((size) => ({
              value: String(size),
              label: t('common.pagination.perPage', { count: size }),
            }))}
            value={String(pageSize)}
            onChange={(val) => {
              onPageSizeChange(Number(val));
              onPageChange?.(0); // Zmiana rozmiaru strony wraca na pierwszą stronę.
            }}
            aria-label={t('common.pagination.perPageAria')}
            className="h-9 w-auto rounded-lg px-3.5"
            data-testid="pagination-page-size"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Generate page numbers to display with ellipsis
 * Returns -1 for ellipsis positions
 */
function generatePageNumbers(currentPage: number, totalPages: number): number[] {
  const pages: number[] = [];
  const showEllipsisThreshold = 7;

  if (totalPages <= showEllipsisThreshold) {
    // Show all pages
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(0);

    if (currentPage > 2) {
      pages.push(-1); // Ellipsis
    }

    // Show pages around current page
    const start = Math.max(1, currentPage - 1);
    const end = Math.min(totalPages - 2, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 3) {
      pages.push(-1); // Ellipsis
    }

    // Always show last page
    pages.push(totalPages - 1);
  }

  return pages;
}
